/** ARV Engine — v1's AI Estimator tab as a collapsible drawer section.
 * Run pulls sold comps from the SAME backend endpoint v1 uses; the median
 * $/sqft of the included ("renovated") set × subject sqft is the ARV. Kick
 * out bad comps and the number recomputes. State (arvResult / arvRehab / the
 * applied arv) lives in the shared LEAD_EDITS keys, so v1's AI tab shows the
 * same analysis. */
import { useMemo, useState } from "react";
import { moneyExact } from "../../lib/money";
import type { Lead } from "../../types";
import { fmtMoneyEdit } from "../edits/api";
import { ARV_MSG, arvCompute, arvMao, narrowArvResult, type ArvResult } from "./api";

const usd = (n: number): string => moneyExact(n);

export function ArvSection({
  lead,
  fields,
  onRun,
  onSaveResult,
  onField,
  startOpen = false,
}: {
  lead: Lead;
  fields: Record<string, unknown> | undefined;
  /** app-level runner (token-bound backendFetch) */
  onRun: (address: string, sqft: number, beds: number | null, onWake: () => void) => Promise<ArvResult>;
  onSaveResult: (address: string, result: ArvResult) => void;
  /** the generic LEAD_EDITS writer — arvRehab + the applied arv go through it */
  onField: (address: string, key: string, value: string | null) => void;
  /** v1 shows the estimator inline in the AI tab — open on mount. */
  startOpen?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // local overlay so toggles paint instantly; edits stay the source of truth
  const [local, setLocal] = useState<ArvResult | null>(null);

  const saved = useMemo(() => narrowArvResult(fields?.["arvResult"]), [fields]);
  const result = local ?? saved;
  const computed = useMemo(() => (result ? arvCompute(result) : null), [result]);
  const rehabRaw = typeof fields?.["arvRehab"] === "string" ? (fields["arvRehab"] as string) : "";
  const [rehabText, setRehabText] = useState(rehabRaw);
  const mao = arvMao(computed?.arv ?? null, rehabText || rehabRaw);

  const sqft = lead.sqft ?? (() => {
    const f = parseFloat(String(fields?.["sqft"] ?? "").replace(/[^0-9.]/g, ""));
    return f > 0 ? f : null;
  })();

  const run = async () => {
    if (busy) return;
    if (!sqft) {
      setNote(ARV_MSG.needSqft);
      return;
    }
    setBusy(true);
    setNote("Pulling sold comps near " + (lead.address.split(",")[0] ?? "") + "…");
    try {
      const r = await onRun(lead.address, sqft, lead.beds, () => setNote("Backend waking up (free server) — retrying in 10s…"));
      setLocal(r);
      onSaveResult(lead.address, r);
      setNote(null);
    } catch (e) {
      setNote(e instanceof Error && e.message ? e.message : ARV_MSG.unreachable);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (i: number) => {
    if (!result) return;
    const next: ArvResult = { ...result, comps: result.comps.map((c, ix) => (ix === i ? { ...c, included: !c.included } : c)) };
    setLocal(next);
    onSaveResult(lead.address, next);
  };

  const commitRehab = () => {
    const v = rehabText.trim();
    if (v === rehabRaw) return;
    onField(lead.address, "arvRehab", v === "" ? null : v);
  };

  const apply = () => {
    if (!computed?.arv) return;
    onField(lead.address, "arv", fmtMoneyEdit(computed.arv));
    setNote(ARV_MSG.saved);
  };

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>✨ ARV Engine</span>
        <span className="sub">
          {computed?.arv ? `ARV ${usd(computed.arv)} · ${computed.sel} comps · ` : saved ? "saved analysis · " : ""}
        </span>
      </div>
        <div className="secbody arvbody">
          <div className="arvintro">
            <span className="sub">
              Recently-sold comps near this property — median $/sqft of the renovated set × your sqft. Kick out bad
              comps; the number recalculates.
            </span>
            <button className="btn primary" disabled={busy} onClick={() => void run()}>
              {busy ? "Pulling solds…" : result ? "Re-run analysis" : "Run ARV analysis"}
            </button>
          </div>
          {note ? <div className="cmpnote">{note}</div> : null}
          {result && computed ? (
            <>
              <div className="arvtiles">
                <div className="arvtile main">
                  <span className="lbl">ARV — {computed.sel} comp{computed.sel !== 1 ? "s" : ""} selected</span>
                  <strong>{computed.arv ? usd(computed.arv) : "—"}</strong>
                  <em>
                    {computed.arv && computed.low !== null && computed.high !== null
                      ? `range ${usd(computed.low)} – ${usd(computed.high)} · ${usd(computed.ppsf ?? 0)}/sqft × ${(result.subject.sqft || 0).toLocaleString("en-US")} sqft`
                      : "select at least one comp"}
                  </em>
                </div>
                <div className="arvtile">
                  <span className="lbl">Rehab estimate</span>
                  <input
                    value={rehabText}
                    placeholder="$45,000"
                    onChange={(e) => setRehabText(e.target.value)}
                    onBlur={commitRehab}
                    onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                  />
                  <em>used for the 70% rule</em>
                </div>
                <div className="arvtile dark">
                  <span className="lbl">Max offer (70% rule)</span>
                  <strong>{mao !== null ? usd(Math.max(mao, 0)) : "—"}</strong>
                  <em>70% × ARV − rehab</em>
                </div>
              </div>
              <div className="arvmeta">
                <span className="sub">
                  {result.comps.length} comparable solds · sources: {result.sources.join(" + ") || "—"} · confidence{" "}
                  <strong>{result.confidence ?? "—"}</strong>
                </span>
                <button className="btn arvapply" disabled={!computed.arv} onClick={apply}>
                  ✓ Save ARV to lead
                </button>
              </div>
              <div className="arvgrid">
                {result.comps.map((c, i) => (
                  <div key={c.address + i} className={"arvcard" + (c.included ? " isin" : " isout")}>
                    {c.photo ? (
                      <img src={c.photo} loading="lazy" alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                    ) : null}
                    <div className="arvcardbody">
                      <div className="arvcardtop">
                        <span className="arvaddr">
                          {c.link ? (
                            <a href={c.link} target="_blank" rel="noreferrer">
                              {(c.address || "").split(",")[0]}
                            </a>
                          ) : (
                            (c.address || "").split(",")[0]
                          )}
                        </span>
                        <input type="checkbox" checked={c.included} onChange={() => toggle(i)} title={c.included ? "Kick out of the set" : "Include in the set"} />
                      </div>
                      <div className="arvprice">
                        {usd(c.price)} <span className="ppsf">· {usd(c.ppsf)}/sqft</span>
                      </div>
                      <div className="cmpmeta">
                        {c.beds ?? "—"}bd/{c.baths ?? "—"}ba · {(c.sqft || 0).toLocaleString("en-US")} sqft
                        {c.sold_date ? " · sold " + String(c.sold_date).slice(0, 10) : ""} ·{" "}
                        <span className="cmpsrc">{c.source}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="arvempty">
              <div className="sub">No analysis yet</div>
              <span className="sub dim">Pulls sold comps from Zillow + Realtor.com (needs the lead's sqft — enrich first).</span>
            </div>
          )}
        </div>
    </div>
  );
}
